/**
 * Local type definitions for the parts of the Jitsi iframe API the moderation panel depends on.
 *
 * The installed `@jitsi/react-sdk` types (`node_modules/@jitsi/react-sdk/lib/types/
 * IJitsiMeetExternalApi.d.ts`) declare `executeCommand` and `getParticipantsInfo` as fully
 * untyped (`(...args: any[]) => void` / `() => object[]`), and `IJitsiMeetExternalApi` extends
 * Node's `EventEmitter` without redeclaring any event payload shapes. None of the interfaces
 * below are confirmed against a live conference on our pinned jitsi-meet tag — see
 * `.plans/in-progress/jitsi-platform/phase3/moderation-panel-design.md` §3/§7 for the verified
 * command list and the open spike items. Treat every field as possibly absent and narrow
 * defensively before use — do not assume anything beyond what's listed here.
 */

/** Shape of a single entry returned by `apiRef.getParticipantsInfo()` (verified fields against
 * stable/jitsi-meet_11031, external_api.js:1071-1080). NOTE: this snapshot has NO `role`/
 * `isModerator` field and NO guest/affiliation marker — moderator status must be tracked purely
 * via `participantRoleChanged` events (plus the initial role on `video-conference-joined`), and
 * guest detection is not available from the iframe API at all (ow2-chat-be#218). */
export interface JitsiParticipantInfo {
  participantId?: string;
  displayName?: string;
  formattedDisplayName?: string;
  avatarURL?: string;
  email?: string;
}

export interface VideoConferenceJoinedEvent {
  roomName?: string;
  id?: string;
  displayName?: string;
  avatarURL?: string;
  // Valid initial-role source: the local participant's role IS present on this event's payload
  // (verified against stable/jitsi-meet_11031). This is the only role read allowed outside of
  // `participantRoleChanged` events.
  role?: string;
}

export interface ParticipantJoinedEvent {
  id?: string;
  displayName?: string;
  avatarURL?: string;
  email?: string;
}

export interface ParticipantLeftEvent {
  id?: string;
}

export interface ParticipantRoleChangedEvent {
  id?: string;
  role?: string;
}

export interface ModerationStatusChangedEvent {
  mediaType?: string;
  enabled?: boolean;
}

/** The string jitsi-meet uses to report a participant holding the moderator role.
 * Confirmed against jitsi-meet source `react/features/base/participants/constants.ts`
 * (`PARTICIPANT_ROLE.MODERATOR = 'moderator'`) at the pinned stable-11031 release. */
export const JITSI_MODERATOR_ROLE = "moderator";
